/**
 * May this caller read the knowledge base at all?
 *
 * ONE PERMISSION, unlike `canReadEscalations`/`canReadJustifications` (four
 * each) — `KnowledgeBaseArticlePolicy::viewAny()`/`::view()` on the server
 * gate on `knowledge.view` alone (that policy's own class docblock: "one
 * `PermissionResolver::has()` check each, no model parameter anywhere").
 * There is no second permission that implies read access the way
 * `justification.decide` implies `justification.view` — `knowledge.manage`
 * holders reach the FAQ through `knowledge.view`, which
 * `RolePermissionSeeder::MATRIX` grants them alongside `knowledge.manage`
 * (both `administrator` and `supervisor` hold both), not as a fallback this
 * helper has to encode.
 *
 * Written as its own function anyway, rather than an inline
 * `permissions.includes('knowledge.view')` at each call site — the identical
 * discipline `canReadEscalations`/`canReadJustifications` document: this is
 * imported by both the nav gate (`App.tsx`) and the screen
 * (`ArticleList.tsx`), never copied into either. If the client and the
 * server (`KnowledgeBaseArticlePolicy::viewAny()`) ever disagree, the
 * symptom is a nav entry or panel that 403s, or a screen nobody can reach —
 * so keep this in step with the server's.
 *
 * `knowledge.view` IS GRANTED TO ALL FIVE ROLES TODAY
 * (`RolePermissionSeeder::MATRIX`) — administrator, supervisor, agent,
 * pharmacist and educator (spec K-7) — so, unlike `escalation.view`, this is
 * not a permission hidden behind a per-user grant no role holds. Checked
 * directly against the API repository's seeder before writing this file
 * (task brief's own defect-1 warning), not assumed from the permission's
 * name.
 */
export function canReadKnowledge(permissions: readonly string[]): boolean {
  return permissions.includes('knowledge.view');
}

/**
 * May this caller author, publish or remove knowledge-base entries?
 *
 * `KnowledgeBaseArticlePolicy::create()`/`::update()`/`::delete()` all gate
 * on this ONE permission (that policy's own docblock: "one permission
 * governs all three"). Granted to `administrator` and `supervisor` only
 * (spec K-7).
 *
 * Used to gate the SCREEN-LEVEL affordances that have no per-row shape to
 * read a server-published ability from — the Draft/Published filter, the
 * "New article" button, and whether `published` is ever put on the outgoing
 * query at all (spec §2/§5: honouring the filter for a non-manage caller
 * would leak which rows are drafts). A ROW's own edit/delete controls are
 * gated on that row's own `can.manage` from `KnowledgeBaseArticleResource`
 * instead — never re-derived from this permission plus the row's state,
 * the Global Constraint `JustificationDetail.tsx`'s own `record.can.answer`/
 * `record.can.decide` discipline states.
 */
export function canManageKnowledge(permissions: readonly string[]): boolean {
  return permissions.includes('knowledge.manage');
}
